import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";

/**
 * PublishConfirmDialog — PHASE 8.35 rewrite
 *
 * Ultra-simple publish flow:
 *   1) Always flips the wave's status from draft → active.
 *   2) Sends the wave_published_complete email to the COMPLETE cohort only.
 *   3) Two radios: "sector-matched" (default) vs "ALL complete patrons" (override).
 *
 * Incomplete + waitlist cohorts are intentionally NOT handled here. Those have
 * their own follow-up buttons on the wave detail page (gated by the
 * `wave_extra_cohort_emails_enabled` CMS toggle), each with its own template
 * and routing.
 *
 * Dedup is server-side via `wave_cohort_sends` — re-publishing the same wave
 * never double-emails any patron.
 */
export default function PublishConfirmDialog({ open, onClose, wave, onPublished }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [matchSector, setMatchSector] = useState(true);     // default: sector-targeted
  const [sendEmail, setSendEmail] = useState(true);         // off → just flip status
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !wave?.id) return undefined;
    let cancelled = false;
    setSummary(null);
    setLoading(true);
    const load = async () => {
      try {
        // Two parallel reads:
        //   /admin/waves/:id/cohort-summary?match_sector=...  → totals + dedup
        //   match_sector swaps between sector-targeted vs unrestricted complete totals
        const r1 = await api.get(`/admin/waves/${wave.id}/cohort-summary`, { params: { match_sector: true } });
        const r2 = await api.get(`/admin/waves/${wave.id}/cohort-summary`, { params: { match_sector: false } });
        if (cancelled) return;
        setSummary({ targeted: r1.data, all: r2.data });
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Could not load cohort counts");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, wave?.id]);

  const submit = async () => {
    setBusy(true);
    try {
      // STEP 1 — always flip status to active. Server is idempotent on re-publish.
      await api.post(`/waves/${wave.id}/publish?notify=false`);

      // STEP 2 — optionally email the complete cohort.
      let emailedCount = 0;
      if (sendEmail) {
        const r = await api.post(`/admin/waves/${wave.id}/send-cohort`, {
          cohort: "complete",
          mode: "all",
          match_sector: matchSector,
        });
        emailedCount = r.data?.sent || 0;
      }
      toast.success(
        sendEmail
          ? `Published. Emailed ${emailedCount} complete-profile patron${emailedCount === 1 ? "" : "s"}.`
          : "Published silently — no emails sent."
      );
      onPublished && onPublished();
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to publish");
    } finally {
      setBusy(false);
    }
  };

  // Pick the right counts based on the radio choice. Server dedups against
  // already-sent users, so `not_yet_sent` is the number actually emailed.
  const sel = (matchSector ? summary?.targeted : summary?.all)?.complete;
  const willEmail = sel?.not_yet_sent ?? 0;
  const alreadySent = sel?.already_sent ?? 0;
  const totalComplete = sel?.total ?? 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-lg rounded-none border-2 border-black" data-testid="publish-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black tracking-tight">Publish wave</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="border-l-4 border-mango pl-3">
            <div className="font-bold" data-testid="publish-wave-name">{wave?.name || "Untitled wave"}</div>
            <div className="text-xs text-neutral-600 mt-0.5">
              Sets status to <span className="font-mono">active</span> and (optionally) emails the complete-profile cohort.
            </div>
          </div>

          {loading && <div className="text-neutral-500 text-xs">Counting matching patrons…</div>}

          {!loading && summary && (
            <>
              {/* Audience choice. Single radio group — sector match vs all. */}
              <div className="space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500 flex items-center justify-between">
                  <span>Audience (complete profiles only)</span>
                  <span className="font-mono normal-case tracking-normal text-neutral-400">
                    Same counts as /admin/users → Complete profile tab
                  </span>
                </div>
                <label
                  className={`flex items-start gap-3 p-3 border-2 cursor-pointer ${matchSector ? "border-mango bg-mango/5" : "border-neutral-300 bg-white"}`}
                  data-testid="publish-radio-targeted"
                >
                  <input
                    type="radio"
                    name="audience"
                    checked={matchSector}
                    onChange={() => setMatchSector(true)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-bold text-sm">Sector-matched complete patrons — {summary.targeted?.complete?.total ?? 0}</div>
                    <div className="text-xs text-neutral-600 mt-0.5">
                      Default. Skips complete patrons whose <span className="font-mono">sectors_of_interest</span> don&apos;t overlap with this wave&apos;s sector.
                    </div>
                  </div>
                </label>
                <label
                  className={`flex items-start gap-3 p-3 border-2 cursor-pointer ${!matchSector ? "border-mango bg-mango/5" : "border-neutral-300 bg-white"}`}
                  data-testid="publish-radio-all"
                >
                  <input
                    type="radio"
                    name="audience"
                    checked={!matchSector}
                    onChange={() => setMatchSector(false)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-bold text-sm">All complete patrons — {summary.all?.complete?.total ?? 0}</div>
                    <div className="text-xs text-neutral-600 mt-0.5">
                      Ignore sector targeting. Use for cross-sector waves (e.g. national-issue surveys).
                    </div>
                  </div>
                </label>
              </div>

              {/* Dedup hint */}
              {alreadySent > 0 && (
                <div className="text-[11px] text-neutral-500 pl-1">
                  {alreadySent} patron{alreadySent === 1 ? " was" : "s were"} already emailed about this wave — they&apos;ll be skipped automatically.
                </div>
              )}

              {/* Skip-email override */}
              <label
                className="flex items-center gap-3 p-3 border border-dashed border-neutral-400 bg-neutral-50 cursor-pointer"
                data-testid="publish-skip-email"
              >
                <input
                  type="checkbox"
                  checked={!sendEmail}
                  onChange={(e) => setSendEmail(!e.target.checked)}
                  className="w-4 h-4"
                />
                <div className="text-xs">
                  <div className="font-bold">Skip email — just mark wave as published</div>
                  <div className="text-neutral-600 mt-0.5">Useful when you want to email manually later (or test the funnel from the wave detail page).</div>
                </div>
              </label>

              <div className="text-[11px] text-neutral-500">
                Incomplete + waitlist cohorts have their own send buttons on the wave detail page (each with a dedicated template &amp; routing).
              </div>
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-none" data-testid="publish-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || loading} className="bg-black hover:bg-neutral-800 text-white rounded-none" data-testid="publish-confirm">
            {busy ? "Publishing…"
              : !sendEmail ? "Publish silently"
              : `Publish & email ${willEmail}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
